import React from 'react';
import { University } from '../../../types';

interface PostCollegeTabProps {
  university: University;
}

const PostCollegeTab: React.FC<PostCollegeTabProps> = ({ university }) => {
  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format percentage (for values that are already percentages like graduation_rate)
  const formatPercentage = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `${Math.round(num)}%`;
  };

  // Format decimal to percentage (for values like employed_2_years_after_graduation)
  const formatDecimalToPercentage = (decimal: number | null | undefined) => {
    if (decimal === null || decimal === undefined) return 'N/A';
    return `${Math.round(decimal * 100)}%`;
  };

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>Post-College Life</h3>
      
      {/* Career Outcomes Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Graduation Rate Card */}
        <div className="bg-green-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 text-2xl mr-3">
              <i className="fas fa-graduation-cap"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Graduation Rate</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{formatPercentage(university.graduation_rate)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>4-year completion</p>
          </div>
        </div>

        {/* Employment Rate Card */}
        <div className="bg-blue-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 text-2xl mr-3">
              <i className="fas fa-briefcase"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Employment Rate</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{formatDecimalToPercentage(university.employed_2_years_after_graduation)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Within 6 months</p>
          </div>
        </div>

        {/* Average Income Card */}
        <div className="bg-purple-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-purple-500 text-2xl mr-3">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Average Income</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{formatCurrency(university.median_earnings_6_years_after_graduation)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Starting salary</p>
          </div>
        </div>
      </div>

      {/* Career Success Section */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-orange-500 text-2xl mr-3">
            <i className="fas fa-chart-line"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Career Success Metrics</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-orange-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Top Employers</h5>
            <p className="text-lg font-bold text-orange-600">Google, Apple, Microsoft</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Leading tech companies</p>
          </div>
          <div className="text-center p-4 bg-orange-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Graduate School</h5>
            <p className="text-lg font-bold text-orange-600">35%</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Pursue advanced degrees</p>
          </div>
        </div>
      </div>

      {/* Alumni Success Stories */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 text-xl mr-3 mt-1">
            <i className="fas fa-trophy"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 mb-3">Alumni Success</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800">
              <div>
                <p className="font-semibold mb-2">Notable Alumni:</p>
                <ul className="space-y-1 text-sm">
                  <li>• 30+ Nobel Prize winners</li>
                  <li>• 17 astronauts</li>
                  <li>• 4 U.S. Presidents</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Career Impact:</p>
                <ul className="space-y-1 text-sm">
                  <li>• {formatDecimalToPercentage(university.employed_2_years_after_graduation)} employed within 6 months</li>
                  <li>• {formatCurrency(university.median_earnings_6_years_after_graduation)} average starting salary</li>
                  <li>• {formatPercentage(university.graduation_rate)} graduation rate</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCollegeTab;
